import { createSelector } from 'reselect'

import {
  ALL_COMPANIES_OPTION,
  findAndHighlightFirstInactive,
  generateCompanySelectorObject,
  statusAndNameSortCallback,
} from 'utils/partnerUtils'
import { atLeastCorePlan, exactCompleteProtectPlan } from 'utils/plans'

import { ENGLISH_LANGUAGE, PLAN_TYPES } from 'containers/App/constants'

import { selectCompanyFilter } from '../PartnerOptions/selectors'

const emptyArray = []

export const selectLoader = (state) => state.app.loader
export const selectError = (state) => state.app.error
export const selectIsDataLoaded = (state) => state.app.isDataLoaded

export const selectData = (state) => state.app.data
export const selectCompaniesData = (state) => state.app.data.partner.companies_data

export const selectBrand = (state) => state.app.data.brand
/**
 * This field will exist also for multi tenants
 */
export const selectPartner = (state) => state.app.data.partner
export const selectSortedCompanies = (state) => {
  if (!state.app.data.partner) return emptyArray
  const companies = state.app.data.partner.all_companies
  return companies.sort(statusAndNameSortCallback(true))
}
export const selectSortedCompaniesOptions = createSelector(selectSortedCompanies, (items) => {
  if (items.length) findAndHighlightFirstInactive(items)
  return items.map((elem) => ({ value: elem.id, label: elem.name }))
})
export const selectCompanySelectorObject = (state) => {
  if (!state.app.data.partner) return {}
  const companyId = state.app.data.id
  const companies = state.app.data.partner.all_companies
  return generateCompanySelectorObject(companyId, companies)
}

export const selectNotifications = (state) => state.app.data.notifications

export const selectUser = (state) => state.app.data.user
export const selectUserId = (state) => state.app.data.user.id
export const selectUserEmail = (state) => state.app.data.user.email

/**
 * selectSelf* selectors don't support MSP case
 * You have to use them only when you're interested
 * in only signed-in user's company data.
 * ATTENTION: in 99% of cases you have to use selectActual* selectors.
 */
export const selectSelfCompanyId = (state) => state.app.data.id
export const selectSelfCompanyName = (state) => state.app.data.name
export const selectSelfCompanyDomain = (state) => state.app.data.domain
export const selectSelfLicenses = (state) => state.app.data.licenses
export const selectSelfPermissions = (state) => state.app.data.permissions
export const selectSelfIntegrations = (state) => state.app.data.integrations
export const selectSelfFeatureFlags = (state) => state.app.data.ff
export const selectSelfOwnerStatus = (state) => state.app.data.owner
export const selectSelfIsPartner = (state) => state.app.data.isPartner
export const selectSelfPartnerName = (state) => state.app.data.partnerName
export const selectSelfBrandName = (state) => state.app.data.brandName

/**
 * selectActual* selectors support MSP case.
 * Returning data for MSP selected company.
 * For not MSP case returning self data.
 */
export const selectActualCompanyId = (state) =>
  selectCompanyFilter(state)?.value || selectSelfCompanyId(state)

const selectRelevantCompany = createSelector(
  selectActualCompanyId,
  selectCompaniesData,
  (id, companiesData) => companiesData.find((c) => c.id === id)
)

const selectActual = (state, field) => {
  const id = selectActualCompanyId(state)
  if (!state.app.data.partner || id === selectSelfCompanyId(state)) return state.app.data[field]
  else {
    const relevantCompany = selectRelevantCompany(state)

    // data is still loading
    if (!relevantCompany) {
      // console.log(
      //   `Provided self company (${state.app.data.id}) data while data of ${id} is loading`
      // )
      return state.app.data[field]
    }

    return relevantCompany[field]
  }
}

export const selectActualLicenses = (state) => selectActual(state, 'licenses')
export const selectActualPermissions = (state) => selectActual(state, 'permissions')
export const selectActualCompanyName = (state) => selectActual(state, 'name')
export const selectActualIntegrations = (state) => selectActual(state, 'integrations')
export const selectActualFeatureFlags = (state) => selectActual(state, 'ff')
export const selectActualTags = (state) => selectActual(state, 'tags')
export const selectActualOwnerStatus = (state) => selectActual(state, 'owner')
export const selectActualLanguages = (state) => selectActual(state, 'languages')
export const selectActualEnabledLanguages = createSelector(selectActualLanguages, (items) =>
  items.filter((l) => l.enabled)
)
export const selectActualEnabledLanguagesOptions = createSelector(
  selectActualEnabledLanguages,
  (items) => items.map((elem) => ({ value: elem.id, label: elem.name }))
)
export const selectActualDefaultLanguage = createSelector(selectActualLanguages, (items) =>
  items.find((l) => l.default)
)
export const selectActualWorkdays = (state) => selectActual(state, 'workdays')
export const selectActualTimezone = (state) => selectActual(state, 'timezone')
export const selectActualScanBackStatus = (state) => selectActual(state, 'scanBackStatus')
export const selectActualShowReintegratePopup = (state) =>
  selectActual(state, 'showReintegrationPopup')

export const selectAllActualProperties = createSelector(
  selectActualLicenses,
  selectActualPermissions,
  selectActualIntegrations,
  selectActualFeatureFlags,
  (licenses, permissions, integrations, ff) => ({
    licenses,
    permissions,
    integrations,
    ff,
  })
)

export const selectActualBrandName = (state) => selectActual(state, 'brandName')
export const selectActualIsPartner = (state) => selectActual(state, 'isPartner')

export const selectActualHasLimitedPocView = (state) => selectActual(state, 'poc_limit')

export const selectActualMicDefenderVisibilityEligible = (state) =>
  selectActual(state, 'micDefenderVisibilityEligible')

/**
 * Returning name of partner above in hierarchy.
 * For example for Vector (767) will return Pax8 (733)
 */
export const selectActualPartnerName = (state) => selectActual(state, 'partnerName')

// ONPREMISE, PROD, STAGING
export const selectEnvironment = (state) => state.app.data.environment
export const selectIsOnPremise = (state) => state.app.data.environment === 'ONPREMISE'
// US, EU, OTHER
export const selectRegion = (state) => state.app.data.region

export const selectIsMaster = (state) => state.app.data.master
export const selectMasterFeatures = (state) => state.app.data.masterFeatures || {}

export const selectIsAdmin = (state) => state.app.data.permissions.admin

export const selectActualMailboxLimit = (state) => selectActual(state, 'mailboxLimit')
export const selectActualMailboxEnabled = (state) => selectActual(state, 'mailboxEnabled')

export const selectStatusOfIrontrapsLicensesAndPerm = (state) =>
  atLeastCorePlan(state.app.data.licenses.plan) &&
  (state.app.data.permissions.irontraps_viewer ||
    state.app.data.permissions.manage_irontraps ||
    state.app.data.permissions.irontraps_action)

export const selectIsAPIGenerated = (state) => state.app.data.is_api_generated

export const selectLastProductRelease = (state) => state.app.data.last_product_release
export const selectLastSeenProductRelease = (state) => state.app.data.product_release_last_seen_date
export const selectDisablePremiumModal = (state) => state.app.data.disable_premium_popup
export const selectIsPax8Hierarchy = (state) => state.app.data.isPax8Hierarchy
export const selectIsFirstCampaignCreated = (state) =>
  state.app.data.setupStatus.created_first_campaign
export const selectIsIneligibleTrainingCampaignsAccount = createSelector(
  selectActualFeatureFlags,
  selectActualLicenses,
  (ff, licenses) =>
    !ff.TRAINING_CAMPAIGNS &&
    ![PLAN_TYPES.SAT_SUITE, PLAN_TYPES.COMPLETE_PROTECT].includes(licenses.plan)
)
export const selectIsIneligibleSpearPhishingAccount = createSelector(
  selectActualFeatureFlags,
  selectActualEnabledLanguages,
  selectActualLicenses,
  (ff, enabledLanguages, licenses) => {
    const isEnglishLanguageEnabled = !!enabledLanguages.find(
      ({ name }) => name === ENGLISH_LANGUAGE.name
    )
    const isFeatureEnabled = ff.SPEAR_PHISHING_CAMPAIGNS || exactCompleteProtectPlan(licenses.plan)

    return !isFeatureEnabled || !isEnglishLanguageEnabled
  }
)
export const selectIsAllCompaniesSelected = (state) =>
  selectPartner(state) && selectCompanyFilter(state)?.value === ALL_COMPANIES_OPTION.value
export const selectEula = (state) => state.app.data.eula
export const selectIsFirstLogin = (state) => state.app.data.eula.is_first_login
export const selectShowEulaModal = (state) => state.app.data.eula.update_required
export const selectEulaType = (state) => state.app.data.eula.eula_type
export const SelectFirstLoginCountry = (state) => state.app.data.eula.country
export const selectDeepfakeActivationTime = (state) => state.app.data.deepfakeActivationTime

/**
 *
 * @param {Object} state
 * @returns {boolean}
 * @description Checks if the user switched to the static demo user/company.
 * MSP is not supported as the static user/company in this scenario are not MSP.
 */
export const selectIsStaticSalesDemo = (state) => state.app.data.isStaticSalesDemo

export const selectScanbackPeriod = (state) => selectActual(state, 'scanBackPeriod')
export const selectDefaultIncidentDateFilter = (state) =>
  selectActual(state, 'defaultIncidentDateFilter')

export const selectLastScanbackDate = (state) => selectScanbackPeriod(state)?.end_date
